import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    DomSanitizer,
    SafeResourceUrl,
    Title,
} from '@angular/platform-browser';
import { Page, PageChild } from '../../models/page.interface';
import { PageService } from '../../services/page/page.service';
import { PathsService } from '../../services/paths/paths.service';
import { environment } from 'src/environments/environment';
import {
    filter,
    map,
    distinctUntilChanged,
    startWith,
    tap,
} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: '[appContent]',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.scss'],
    standalone: false,
})
export class ContentComponent {
    public tabTitle: string = 'AVEVA Design System / Build';
    public title: string = '';
    public headerImage: boolean = false;
    public headerImageSourceUrl: string = '';
    public headerImageAltText: string = '';
    public content: string = '';
    public guidanceStatus: string = '';
    public slug: string = '';
    public scopePlatform: string = '';
    public childType: string = '';
    public children: PageChild[] = [];
    public selectedIndex: number = 0;
    public page: string = '';
    public pageData!: Page;
    public type: string = '';
    public iframeUrl: string = '';
    public error: string | null = null;
    public readonly researchCards = 'research-cards';
    public readonly researchCard = 'research-card';
    public busy: boolean = true;
    domSanitizer: any;

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly titleService = inject(Title);
    public readonly pageService = inject(PageService);
    private readonly pathsService = inject(PathsService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                map(() => this.router.url),
                startWith(this.router.url),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (url) => {
                    this.pageService.getPageContent(url);
                },
            });

        this.pageService.loading
            .pipe(
                tap((isLoading) => {
                    this.busy = isLoading;
                    if (isLoading) {
                        this.error = null;
                    }
                }),
                filter((isLoading) => !isLoading),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => this.hydrateFromService(),
            });
    }

    private hydrateFromService(): void {
        const page = this.pageService.page;
        const error = this.pageService.error;
        this.error = null;

        this.pageData = page;

        if (error) {
            this.error = error;
            return;
        }

        if (!page?.title) {
            this.error = this.pageService.error;
            return;
        }

        this.titleService.setTitle(`${this.tabTitle} / ${page.title}`);
        this.title = page.title;

        if (page.headerImageSourceUrl === '') {
            this.headerImage = false;
            this.headerImageSourceUrl = '';
        } else {
            this.headerImage = true;
            this.headerImageSourceUrl = this.pathsService.fixImageHeaderPath(
                environment.version,
                page.headerImageSourceUrl,
            );
        }

        this.childType = page.childType;
        this.children = page.children;

        if (this.childType === 'cards') {
            this.children = [...this.children].sort((x, y) =>
                x.title < y.title ? -1 : x.title > y.title ? 1 : 0,
            );
        }

        this.guidanceStatus = page.guidanceStatus;
        this.slug = page.slug;
        this.scopePlatform = page.scopePlatform;
        this.content = page.content;
        this.selectedIndex = page.tabIndex;
        this.page = page.url;
        this.type = page.type;
        this.iframeUrl = page.exampleUrl;
    }

    safeUrl(url: string): SafeResourceUrl | null {
        return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
    }
}
